import React from 'react';

/**
 * Appends the product block to what the staff member has already written (never replaces it),
 * and doesn't add it twice.
 */
export const appendProductBlock = (currentText, block) => {
  const text = currentText ?? '';
  if (!block) return text;
  if (text.includes(block)) return text;
  return text.trim() ? `${text.trimEnd()}\n\n${block}` : block;
};

const formatPrice = (price) =>
  price === null || price === undefined ? null : `₹${Number(price).toLocaleString('en-IN')}`;

const noteStyle = { fontSize: 12, color: '#8a6d3b', fontStyle: 'italic', marginTop: 6 };

/**
 * Real AgriBegri products matched to the crop / pest / disease in the farmer's comment.
 * `data` is the AI suggestions payload from the KisanVani API.
 * Renders nothing when a product recommendation isn't applicable (no pest/disease identified).
 */
function AiProductRecommendations({ data, isLoading, onInsert }) {
  if (isLoading || !data) return null;

  const products = data.recommendedProducts ?? [];
  const problems = data.detectedProblems ?? [];
  if (products.length === 0 && problems.length === 0) return null;

  const matched = [...problems, data.detectedCrop].filter(Boolean).join(' · ');

  return (
    <div
      aria-label="AgriBegri product recommendations"
      style={{
        marginBottom: 10, padding: 10, background: '#fffdf5',
        border: '1px solid #ffe0b2', borderRadius: 8,
      }}
    >
      <div style={{
        fontSize: 12, color: '#555', marginBottom: 8, fontWeight: 500,
        display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
      }}>
        <span>🛒</span>
        <span>AgriBegri Products</span>
        {matched && <span style={{ color: '#888', fontWeight: 400 }}>for {matched}</span>}
      </div>

      {products.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {products.map((p) => (
            <div
              key={p.productId}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: 8,
                background: '#fff', border: '1px solid #eee', borderRadius: 6,
              }}
            >
              {p.imageUrl && (
                <img
                  src={p.imageUrl}
                  alt=""
                  width={40}
                  height={40}
                  style={{ objectFit: 'contain', borderRadius: 4, flexShrink: 0 }}
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>{p.title}</div>
                <div style={{ fontSize: 11, color: '#777' }}>
                  {[p.vendor, formatPrice(p.price)].filter(Boolean).join(' · ')}
                </div>
              </div>
              {p.productUrl && (
                <a
                  href={p.productUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: 12, color: '#2e7d32', whiteSpace: 'nowrap' }}
                >
                  View
                </a>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={noteStyle}>
          No matching AgriBegri products found{problems.length > 0 ? ` for ${problems.join(', ')}` : ''}.
          Nothing unrelated was added.
        </div>
      )}

      {products.length === 1 && (
        <div style={noteStyle}>
          Only 1 close match exists in the AgriBegri catalog. Nothing unrelated was added to reach 2.
        </div>
      )}

      {products.length > 0 && data.recommendedProductsText && (
        <button
          type="button"
          onClick={() => onInsert?.(data.recommendedProductsText)}
          style={{
            marginTop: 8, padding: '5px 12px', background: '#fff3e0',
            border: '1px solid #ffcc80', borderRadius: 16, fontSize: 12,
            color: '#e65100', cursor: 'pointer',
          }}
        >
          Add products to reply
        </button>
      )}
    </div>
  );
}

export default AiProductRecommendations;
